import React from 'react';
import { Card, Typography, Row, Col, Tag, Divider } from 'antd';
import { CodeOutlined, DatabaseOutlined, BranchesOutlined, ToolOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const DomainModelView: React.FC = () => {
  const valueObjects = [
    {
      name: 'Address',
      props: ['country: string', 'city: string', 'street: string', 'postalCode: string'],
      methods: ['toString(): string'],
      description: '地址信息，不可变值对象'
    },
    {
      name: 'Money',
      props: ['amount: number', 'currency: string'],
      methods: ['toString(): string'],
      description: '货币金额，包含数值和币种'
    },
    {
      name: 'Dimensions',
      props: ['length: number', 'width: number', 'height: number', 'unit: string'],
      methods: ['getVolume(): number'],
      description: '货物尺寸，用于计算体积'
    },
    {
      name: 'Route',
      props: ['origin: Address', 'destination: Address', 'transitPorts: Address[]'],
      methods: [],
      description: '运输路线，包含起点、终点和中转港'
    }
  ];

  const entities = [
    {
      name: 'Customer',
      props: [
        'id: string',
        'name: string', 
        'type: string',
        'contactInfo: object',
        'address: Address',
        'createdAt: Date',
        'isActive: boolean'
      ],
      methods: ['updateContactInfo()', 'deactivate()'],
      description: '客户实体，可以是发货人或收货人'
    },
    {
      name: 'Cargo',
      props: [
        'id: string',
        'description: string',
        'weight: number',
        'dimensions: Dimensions',
        'value: Money',
        'dangerousGoods: boolean'
      ],
      methods: ['getVolumetricWeight()', 'getChargeableWeight()'],
      description: '货物实体，包含物理属性和计费信息'
    },
    {
      name: 'Shipment',
      props: [
        'id: string',
        'shipper: Customer',
        'consignee: Customer',
        'cargo: Cargo[]',
        'serviceType: string',
        'route: Route',
        'status: string',
        'bookingDate: Date'
      ],
      methods: ['confirmBooking()', 'startTransit()', 'deliver()', 'calculateTotalCost()'],
      description: '运输单聚合根，管理整个运输过程'
    }
  ];

  const services = [
    {
      name: 'FreightCalculationService',
      methods: ['calculateFreightCost(shipment)', 'validateShipment(shipment)'],
      description: '费用计算和运输单验证的领域服务'
    }
  ];

  const repositories = [
    {
      name: 'ShipmentRepository',
      methods: ['save(shipment)', 'findById(id)', 'findByCustomer(customerId)', 'findByStatus(status)'],
      description: '运输单数据库访问层'
    }
  ];

  const renderObjectCard = (obj: any, type: string, icon: React.ReactNode, color: string) => (
    <Card
      key={obj.name}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          <span style={{ marginLeft: 8 }}>{obj.name}</span>
          <Tag color={color} style={{ marginLeft: 8 }}>{type}</Tag>
        </div>
      }
      style={{ marginBottom: 16, height: '100%' }}
    >
      <Paragraph style={{ fontSize: '13px', marginBottom: 12 }}>
        {obj.description}
      </Paragraph>
      
      {obj.props && (
        <>
          <Text strong>属性：</Text>
          <ul style={{ fontSize: '12px', marginTop: 4, marginBottom: 12 }}>
            {obj.props.map((prop: string, index: number) => (
              <li key={index}><Text code>{prop}</Text></li>
            ))}
          </ul>
        </>
      )}
      
      {obj.methods && obj.methods.length > 0 && (
        <>
          <Text strong>方法：</Text>
          <ul style={{ fontSize: '12px', marginTop: 4 }}>
            {obj.methods.map((method: string, index: number) => (
              <li key={index}><Text code>{method}</Text></li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );

  return (
    <div>
      <Title level={2}>货代业务领域模型</Title>
      <Paragraph>
        这里展示了完整的货代业务领域模型结构，包括值对象、实体、聚合、领域服务和数据库访问层。
        每个组件都按照DDD的设计原则进行建模。
      </Paragraph>

      <Divider orientation="left">
        <CodeOutlined style={{ color: '#52c41a' }} />
        <span style={{ marginLeft: 8 }}>值对象 (Value Objects)</span>
      </Divider>
      <Row gutter={[16, 16]}>
        {valueObjects.map(vo => (
          <Col xs={24} md={12} lg={6} key={vo.name}>
            {renderObjectCard(vo, '值对象', <CodeOutlined style={{ color: '#52c41a' }} />, 'green')}
          </Col>
        ))}
      </Row>

      <Divider orientation="left" style={{ marginTop: 32 }}>
        <DatabaseOutlined style={{ color: '#1890ff' }} />
        <span style={{ marginLeft: 8 }}>实体 (Entities)</span>
      </Divider>
      <Row gutter={[16, 16]}>
        {entities.map(entity => (
          <Col xs={24} lg={8} key={entity.name}>
            {renderObjectCard(entity, '实体', <DatabaseOutlined style={{ color: '#1890ff' }} />, 'blue')}
          </Col>
        ))}
      </Row>

      <Divider orientation="left" style={{ marginTop: 32 }}>
        <ToolOutlined style={{ color: '#fa8c16' }} />
        <span style={{ marginLeft: 8 }}>领域服务 (Domain Services)</span>
      </Divider>
      <Row gutter={[16, 16]}>
        {services.map(service => (
          <Col xs={24} md={12} key={service.name}>
            {renderObjectCard(service, '领域服务', <ToolOutlined style={{ color: '#fa8c16' }} />, 'orange')}
          </Col>
        ))}
      </Row>

      <Divider orientation="left" style={{ marginTop: 32 }}>
        <BranchesOutlined style={{ color: '#eb2f96' }} />
        <span style={{ marginLeft: 8 }}>数据库访问层 (Repositories)</span>
      </Divider>
      <Row gutter={[16, 16]}>
        {repositories.map(repo => (
          <Col xs={24} md={12} key={repo.name}>
            {renderObjectCard(repo, '数据库访问层', <BranchesOutlined style={{ color: '#eb2f96' }} />, 'magenta')}
          </Col>
        ))}
      </Row>

      <Card title="聚合设计说明" style={{ marginTop: 32 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card type="inner" title="Shipment 聚合">
              <Paragraph>
                <Text strong>聚合根：</Text>Shipment
              </Paragraph>
              <Paragraph>
                <Text strong>聚合内实体：</Text>
                <ul>
                  <li>Customer (通过引用)</li>
                  <li>Cargo (包含关系)</li>
                </ul>
              </Paragraph>
              <Paragraph>
                <Text strong>值对象：</Text>Route, Address, Money, Dimensions
              </Paragraph>
              <Paragraph>
                <Text strong>一致性规则：</Text>
                <ul>
                  <li>运输单状态变更必须遵循业务流程</li>
                  <li>费用计算基于货物信息和服务类型</li>
                  <li>危险品不能使用快递服务</li>
                </ul>
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card type="inner" title="设计原则">
              <ul>
                <li><Text strong>单一职责：</Text>每个对象有明确的责任</li>
                <li><Text strong>封装性：</Text>业务规则封装在实体内部</li>
                <li><Text strong>不变性：</Text>值对象创建后不可修改</li>
                <li><Text strong>聚合边界：</Text>通过聚合根访问内部对象</li>
                <li><Text strong>领域服务：</Text>处理跨实体的业务逻辑</li>
              </ul>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DomainModelView;